import { resolvePermissions } from './permissions';
import { safeFetchJSON } from '@charitystack/shared';
import {
  decodeJwtPayloadSafe,
  isAuthDiagnosticsEnabled,
  logAuthDiag,
} from './authDiagnostics';
import { isDevStateLabEnvironment } from '../dev-state-lab/devStateEnvironment';

// Refresh interval
const CLERK_REFRESH_MS = 60 * 1000; // 60 seconds

// Buffer before JWT expiry to trigger proactive refresh (seconds).
// Clerk tokens live ~60s, so 30s buffer means we refresh at the halfway point.
const TOKEN_EXPIRY_BUFFER_S = 30;

// Clerk getToken function reference (set by ClerkAuthWrapper in App.js)
let _clerkGetToken = null;

// Token cache
let _cachedToken = null;
let _refreshTimer = null;
let _inflightRefresh = null; // Shared promise for deduplication
let _refreshGeneration = 0; // Incremented on invalidate to discard stale refresh results

// Groups cache (merchant names extracted from auth session)
let _cachedGroups = null;

// Clerk org role cache (e.g. "org:admin", "org:finance", "org:editor", "org:member")
let _cachedRole = null;

// Effective role key used for permission resolution
// For built-in roles: same as _cachedRole (e.g. "org:finance")
// For custom roles: "custom:<slug>" (e.g. "custom:volunteer_coordinator")
let _cachedEffectiveRole = null;

// Clerk user email (not available in JWT claims, cached from clerkUser object)
let _cachedEmail = null;

// Custom role slug from membership public_metadata.custom_role (null if built-in role)
let _cachedCustomRole = null;

// Custom role definitions from org public_metadata.customRoles
let _cachedCustomRoles = null;

// Permission overrides from Clerk public_metadata: { allow: [...], deny: [...] }
let _cachedOverrides = null;

// Org-level role permissions from Clerk org public_metadata.rolePermissions
let _cachedOrgRolePermissions = null;

// Resolved effective permissions (role defaults + allow - deny)
let _resolvedPermissions = null;

// Dev State Lab overrides are UI-only. They never affect tokens or backend auth.
let _devAuthOverride = null;
let _devResolvedPermissions = null;

function _isDevAuthOverrideAllowed() {
  return isDevStateLabEnvironment();
}

function _resolveDevAuthOverride(override) {
  if (!override || typeof override !== 'object') {
    return null;
  }

  const effectiveRole = override.effectiveRole || override.role || null;
  if (Array.isArray(override.permissions)) {
    return override.permissions;
  }
  if (!effectiveRole) {
    return null;
  }

  return resolvePermissions(
    effectiveRole,
    override.permissionOverrides || null,
    override.rolePermissions || null,
  );
}

export function setDevAuthOverride(override) {
  if (!_isDevAuthOverrideAllowed()) {
    _devAuthOverride = null;
    _devResolvedPermissions = null;
    return;
  }

  _devAuthOverride = override && typeof override === 'object' ? override : null;
  _devResolvedPermissions = _resolveDevAuthOverride(_devAuthOverride);
}

export function clearDevAuthOverride() {
  _devAuthOverride = null;
  _devResolvedPermissions = null;
}

// Refresh token immediately when the tab regains focus.
// Browsers throttle setInterval in background tabs, so the 60s refresh timer
// may not fire for minutes. This ensures a fresh token is ready before any
// API call fires on the now-visible page.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible' &&
      _cachedToken &&
      _isTokenExpiringSoon(_cachedToken)
    ) {
      _refreshToken().catch(() => {});
    }
  });
}

/**
 * Check if a JWT is expired or expiring within TOKEN_EXPIRY_BUFFER_S.
 * Decodes the payload without verification (we only need the `exp` claim).
 */
function _isTokenExpiringSoon(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return true;
    return payload.exp - Date.now() / 1000 < TOKEN_EXPIRY_BUFFER_S;
  } catch {
    return true; // Can't decode → treat as expired
  }
}

/**
 * Fetch a fresh token from Clerk (no caching).
 * Retries up to 3 times with exponential backoff to handle transient
 * Clerk API failures (e.g. 500 on /v1/client/sessions/{id}/tokens).
 */
async function _fetchFreshToken() {
  if (!_clerkGetToken) {
    throw new Error('Clerk not initialized. Ensure component is mounted and authenticated.');
  }

  const MAX_ATTEMPTS = 3;
  const BASE_DELAY_MS = 500;
  let lastError;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const token = await _clerkGetToken();
      if (!token) {
        // Null token means user is not authenticated — no point retrying.
        throw new Error('Failed to get Clerk token. User may not be signed in.');
      }
      if (attempt > 0) {
        console.log(`[Auth] Clerk getToken succeeded on retry ${attempt}`);
      }
      return token;
    } catch (error) {
      // If the token was null (unauthenticated), surface immediately — retrying won't help.
      if (error?.message?.includes('User may not be signed in')) {
        throw error;
      }
      lastError = error;
      if (attempt < MAX_ATTEMPTS - 1) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        console.warn(
          `[Auth] Clerk getToken attempt ${attempt + 1}/${MAX_ATTEMPTS} failed, retrying in ${delay}ms:`,
          error?.message || error,
        );
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Refresh the cached token. Deduplicates concurrent calls — if a refresh
 * is already in-flight, all callers share the same promise.
 */
function _refreshToken() {
  if (_inflightRefresh) {
    return _inflightRefresh;
  }

  const gen = ++_refreshGeneration;
  _inflightRefresh = _fetchFreshToken()
    .then((token) => {
      // Only write if this is still the current generation — a newer
      // invalidateTokenCache() call may have started a fresher refresh.
      if (gen === _refreshGeneration) {
        _cachedToken = token;
        _inflightRefresh = null;
      }
      return token;
    })
    .catch((error) => {
      if (gen === _refreshGeneration) {
        _inflightRefresh = null;
      }
      console.error('[Auth] Token refresh failed:', error);
      throw error;
    });

  return _inflightRefresh;
}

function _startRefreshTimer() {
  _stopRefreshTimer();
  _refreshTimer = setInterval(() => {
    _refreshToken().catch(() => {
      // Swallow — error already logged inside _refreshToken.
      // The old cached token is preserved for in-flight requests.
    });
  }, CLERK_REFRESH_MS);
}

function _stopRefreshTimer() {
  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }
}

/**
 * Store Clerk's getToken function globally and prime the token cache.
 * Called by ClerkAuthWrapper in App.js on sign-in.
 * Pass null on sign-out to clear everything.
 *
 * @param {Function|null} fn - Clerk's getToken function, or null on sign-out
 * @param {string[]} [groups] - Formatted merchant group names
 * @param {string} [role] - Clerk org role (e.g. "org:admin", "org:finance", "org:editor", "org:member")
 * @param {{ allow?: string[], deny?: string[] }} [overrides] - User-level permission overrides from membership public_metadata
 * @param {Object} [orgRolePermissions] - Org-level custom role permissions from org public_metadata.rolePermissions
 * @param {string} [customRole] - Custom role slug from membership public_metadata.custom_role (null for built-in roles)
 * @param {Object} [customRoles] - Custom role definitions from org public_metadata.customRoles
 * @param {string} [email] - User's email address (from clerkUser.primaryEmailAddress, not available in JWT)
 */
export function setClerkGetTokenFn(
  fn,
  groups,
  role,
  overrides,
  orgRolePermissions,
  customRole,
  customRoles,
  email,
) {
  _clerkGetToken = fn;

  if (fn) {
    // New getToken binding (e.g. new active org) — never return a cached JWT from the prior org/session.
    _refreshGeneration += 1;
    _cachedToken = null;
    _inflightRefresh = null;

    // Cache groups from Clerk
    if (groups) {
      _cachedGroups = groups;
    }
    // Cache the Clerk org role for permission lookups. Clear stale role data when
    // a user has no active organization yet (multi-org sign-in picker state).
    _cachedRole = role || null;
    // Cache user email (Clerk JWT v2 doesn't include email claims)
    _cachedEmail = email || null;
    // Cache custom role info
    _cachedCustomRole = customRole || null;
    _cachedCustomRoles = customRoles || null;

    // Determine the effective role key for permission resolution
    // For custom roles: use "custom:<slug>" as the key into orgRolePermissions
    // For built-in roles: use the Clerk role directly (e.g. "org:finance")
    if (customRole && role === 'org:member') {
      _cachedEffectiveRole = `custom:${customRole}`;
    } else {
      _cachedEffectiveRole = role || null;
    }

    // Cache permission overrides and org-level role permissions, then resolve
    _cachedOverrides = overrides || null;
    _cachedOrgRolePermissions = orgRolePermissions || null;
    _resolvedPermissions = _cachedEffectiveRole
      ? resolvePermissions(_cachedEffectiveRole, _cachedOverrides, _cachedOrgRolePermissions)
      : null;

    // Prime the token cache immediately and start the refresh timer
    _refreshToken().catch(() => {});
    _startRefreshTimer();
  } else {
    // Sign-out: clear everything
    _stopRefreshTimer();
    _cachedToken = null;
    _cachedGroups = null;
    _cachedEmail = null;
    _cachedRole = null;
    _cachedEffectiveRole = null;
    _cachedCustomRole = null;
    _cachedCustomRoles = null;
    _cachedOverrides = null;
    _cachedOrgRolePermissions = null;
    _resolvedPermissions = null;
    _inflightRefresh = null;
  }
}

/**
 * Invalidate the cached token so the next getAuthToken() call fetches a fresh one.
 * Use after clerk.setActive() to ensure the new org context is in the JWT.
 * Returns a promise that resolves with the fresh token.
 */
export async function invalidateTokenCache() {
  _cachedToken = null;
  _inflightRefresh = null;
  return _refreshToken();
}

/**
 * Store a JWT obtained directly from Clerk (e.g. session.getToken({ skipCache: true }))
 * after setActive so getAuthToken() returns an org-scoped token immediately.
 *
 * Bumps the refresh generation so any in-flight refresh from a stale getToken closure
 * cannot overwrite this token.
 *
 * @param {string} token - Raw JWT from Clerk
 */
export function primeTokenCache(token) {
  if (!token || typeof token !== 'string') {
    return;
  }
  _refreshGeneration += 1;
  _inflightRefresh = null;
  _cachedToken = token;
}

/**
 * Fetch with automatic retry on 403 (auth context not yet propagated).
 *
 * After clerk.setActive(), the JWT may briefly lack org context. This
 * wrapper retries on 403 with a fresh token.
 *
 * Also logs diagnostic info (JWT orgId) on 403 to help identify root
 * cause of auth failures during onboarding.
 *
 *   403 → log JWT orgId → invalidateTokenCache → wait → retry
 *       └─ up to maxRetries attempts before surfacing the error
 *
 * @param {string} url
 * @param {RequestInit} options - fetch options (method, headers, body).
 *   Authorization header is set automatically (fresh token per attempt).
 * @param {object} config
 * @param {string} config.context - human-readable label for logs
 * @param {number} [config.maxRetries=3] - max retry attempts on 403
 * @param {number} [config.retryDelay=1000] - ms between retries
 * @returns {Promise<{ok, data, error, status}>} - same shape as safeFetchJSON
 */
export async function fetchWithAuthRetry(url, options = {}, config = {}) {
  const { context = 'API call', maxRetries = 3, retryDelay = 1000 } = config;

  let lastResult = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Get a fresh token for each attempt (critical for retries)
    const token = await getAuthToken();
    const fetchOptions = {
      ...options,
      headers: {
        ...options.headers,
        Authorization: token,
      },
    };

    const result = await safeFetchJSON(url, fetchOptions, {
      context,
      retries: 0, // we handle retries ourselves
    });

    if (result.ok) {
      if (attempt > 0) {
        console.log(`[fetchWithAuthRetry] ${context}: succeeded on retry ${attempt}`);
      }
      return result;
    }

    lastResult = result;

    if (
      isAuthDiagnosticsEnabled() &&
      (result.status === 401 || result.status === 403)
    ) {
      logAuthDiag(`fetchWithAuthRetry: ${context}`, {
        attempt: attempt + 1,
        maxRetries: maxRetries + 1,
        status: result.status,
        willRetry403: result.status === 403 && attempt < maxRetries,
        jwtClaims: decodeJwtPayloadSafe(token),
        safeFetchError: result.error ?? null,
      });
    }

    if (result.status === 403 && attempt < maxRetries) {
      // Diagnostic: decode JWT to check if orgId is present
      try {
        const parts = (token || '').split('.');
        if (parts.length === 3) {
          const claims = JSON.parse(atob(parts[1]));
          const orgId = claims?.o?.id || claims?.org_id || '(none)';
          console.warn(
            `[fetchWithAuthRetry] ${context}: 403 on attempt ${attempt + 1}/${maxRetries}`,
            `orgId=${orgId}`,
            `error=${result.error}`,
          );
        }
      } catch {
        console.warn(`[fetchWithAuthRetry] ${context}: 403 on attempt ${attempt + 1} (JWT decode failed)`);
      }

      try {
        await invalidateTokenCache();
      } catch {
        // invalidateTokenCache failure shouldn't break retry loop
      }
      await new Promise((r) => setTimeout(r, retryDelay));
      continue;
    }

    // Non-403 error or max retries exhausted
    return result;
  }

  // Should not reach here, but return last result as safety net
  return lastResult;
}

/**
 * Check if the auth provider is ready to issue tokens.
 * True once setClerkGetTokenFn() has been called with a function.
 */
export function isAuthReady() {
  return !!_clerkGetToken;
}

/**
 * Get a JWT token from Clerk.
 *
 * Returns the cached token instantly when available. The token is refreshed
 * proactively in the background (every 60s) so callers almost never wait
 * for a network round-trip.
 *
 * On the very first call (before the cache is primed), callers will await
 * the initial fetch. Concurrent first-callers share the same promise.
 */
export async function getAuthToken() {
  // Fast path: return cached token if it's still fresh
  if (_cachedToken && !_isTokenExpiringSoon(_cachedToken)) {
    return _cachedToken;
  }

  // Token missing, expired, or expiring soon — refresh
  try {
    const token = await _refreshToken();

    // Start the background refresh timer if not already running
    if (!_refreshTimer) {
      _startRefreshTimer();
    }

    return token;
  } catch {
    // Refresh failed — return stale token as last resort (may still be accepted
    // by API Gateway's authorizer cache). Returning null would break all requests.
    if (_cachedToken) return _cachedToken;
    return null;
  }
}

export function getCachedAuthToken() {
  return _cachedToken && !_isTokenExpiringSoon(_cachedToken) ? _cachedToken : null;
}

/**
 * Get the authenticated user's group names (merchant names).
 *
 * Groups are set by ClerkAuthWrapper via setClerkGetTokenFn().
 *
 * If groups aren't cached yet, this awaits the first token fetch.
 */
export async function getAuthGroups() {
  if (_cachedGroups) {
    return _cachedGroups;
  }

  // Groups not cached yet — trigger a token fetch
  // For Clerk, groups are set via setClerkGetTokenFn before any component mounts
  await getAuthToken();
  return _cachedGroups || [];
}

/**
 * Get the current user's Clerk org role (e.g. "org:admin", "org:finance", "org:editor", "org:member").
 */
export function getUserRole() {
  if (_devAuthOverride?.role) return _devAuthOverride.role;
  if (_devAuthOverride?.effectiveRole && !_devAuthOverride.effectiveRole.startsWith('custom:')) {
    return _devAuthOverride.effectiveRole;
  }
  return _cachedRole;
}

/**
 * Get the current user's email address.
 * Cached from clerkUser.primaryEmailAddress (set via setClerkGetTokenFn).
 * @returns {string|null}
 */
export function getUserEmail() {
  return _cachedEmail;
}

/**
 * Get the effective role key used for permission resolution.
 * For built-in roles: same as getUserRole() (e.g. "org:finance")
 * For custom roles: "custom:<slug>" (e.g. "custom:volunteer_coordinator")
 */
export function getEffectiveRole() {
  if (_devAuthOverride?.effectiveRole) return _devAuthOverride.effectiveRole;
  return _cachedEffectiveRole;
}

/**
 * Get the current user's custom role slug (e.g. "volunteer_coordinator").
 * Returns null if the user has a built-in role.
 */
export function getCustomRole() {
  if (_devAuthOverride?.customRole !== undefined) return _devAuthOverride.customRole;
  return _cachedCustomRole;
}

/**
 * Get the org's custom role definitions from public_metadata.customRoles.
 * @returns {Object|null} e.g. { "volunteer_coordinator": { label: "Volunteer Coordinator", createdAt: "..." } }
 */
export function getCustomRoles() {
  return _cachedCustomRoles;
}

/**
 * Get the current user's effective permission list (role defaults + overrides).
 */
export function getUserPermissions() {
  if (_devResolvedPermissions) return _devResolvedPermissions;
  return _resolvedPermissions || [];
}

/**
 * Check whether the current user has a specific permission.
 * Uses resolved permissions (role defaults → allow overrides → deny overrides).
 * @param {string} permission - Permission key (use P.* constants from permissions.js)
 * @returns {boolean}
 */
export function checkPermission(permission) {
  if (_devResolvedPermissions) return _devResolvedPermissions.includes(permission);
  if (!_resolvedPermissions) return false;
  return _resolvedPermissions.includes(permission);
}

/**
 * Update org-level role permissions and recalculate resolved permissions.
 * Called after an admin saves changes in the PermissionsManagement UI.
 * @param {Object|null} orgRolePermissions - e.g. { "org:finance": [...], "org:editor": [...], "custom:volunteer_coordinator": [...] }
 */
export function setOrgRolePermissions(orgRolePermissions) {
  _cachedOrgRolePermissions = orgRolePermissions || null;
  if (_cachedEffectiveRole) {
    _resolvedPermissions = resolvePermissions(
      _cachedEffectiveRole,
      _cachedOverrides,
      _cachedOrgRolePermissions,
    );
  }
}

/**
 * Update cached custom roles definitions (after admin creates/deletes a custom role).
 * @param {Object|null} customRoles - e.g. { "volunteer_coordinator": { label: "...", createdAt: "..." } }
 */
export function setCustomRoles(customRoles) {
  _cachedCustomRoles = customRoles || null;
}

/**
 * Get the cached org-level role permissions.
 * @returns {Object|null}
 */
export function getOrgRolePermissions() {
  return _cachedOrgRolePermissions;
}

/**
 * Dev-only: inspect cached token claims without logging the raw JWT.
 * @returns {{ isAuthReady: boolean, cachedJwtClaims: object|null }|null}
 */
export function getAuthDiagnosticsSnapshot() {
  if (!isAuthDiagnosticsEnabled()) return null;
  return {
    isAuthReady: !!_clerkGetToken,
    cachedJwtClaims: decodeJwtPayloadSafe(_cachedToken),
  };
}
